export const productionAppOrigin = 'https://crm.resilienceza.com';
export const productionApiOrigin = 'https://resilience-crm-api-production.brenden-41c.workers.dev';

interface ProductionTarget {
  projectRef?: string | undefined;
  databaseUrl?: string | undefined;
  publicKey?: string | undefined;
  appOrigin?: string | undefined;
  apiOrigin?: string | undefined;
}

// This detects accidental privileged-key placement; it does not authenticate a JWT.
function isPublicKey(key: string | undefined): boolean {
  if (!key) return false;
  if (/^sb_publishable_[A-Za-z0-9_-]+$/.test(key)) return true;
  const parts = key.split('.');
  if (parts.length !== 3 || parts.some((part) => !/^[A-Za-z0-9_-]+$/.test(part))) return false;
  try {
    const payload: unknown = JSON.parse(atob(parts[1]!.replaceAll('-', '+').replaceAll('_', '/')));
    return (
      typeof payload === 'object' &&
      payload !== null &&
      'role' in payload &&
      payload.role === 'anon'
    );
  } catch {
    return false;
  }
}

export function isProductionTarget(target: ProductionTarget): boolean {
  return Boolean(
    target.projectRef &&
    /^[a-z0-9]{20}$/.test(target.projectRef) &&
    target.projectRef !== 'fzlsiybegwyqkilrqopr' &&
    target.databaseUrl === `https://${target.projectRef}.supabase.co` &&
    target.appOrigin === productionAppOrigin &&
    (target.apiOrigin === undefined || target.apiOrigin === productionApiOrigin) &&
    isPublicKey(target.publicKey),
  );
}

export function assertProductionWebEnvironment(
  environment: Record<string, string | undefined>,
): void {
  if (environment.VITE_APP_ENV !== 'production') return;
  if (
    !environment.VITE_API_URL ||
    !isProductionTarget({
      projectRef: environment.VITE_SUPABASE_PROJECT_REF,
      databaseUrl: environment.VITE_SUPABASE_URL,
      publicKey: environment.VITE_SUPABASE_ANON_KEY,
      appOrigin: productionAppOrigin,
      apiOrigin: environment.VITE_API_URL,
    }) ||
    !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
      environment.VITE_AZURE_TENANT_ID ?? '',
    )
  ) {
    // Never include supplied configuration or credentials in build output.
    throw new Error('Production web target is incomplete or unsafe');
  }
}

export function canStartBrowserApplication(
  origin: string,
  environment: Record<string, string | undefined>,
): boolean {
  if (origin === productionAppOrigin && environment.VITE_APP_ENV !== 'production') return false;
  try {
    assertProductionWebEnvironment(environment);
    return true;
  } catch {
    return false;
  }
}
